"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  Notice,
  Panel,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  PlainLanguagePanel,
  StatusList,
} from "@/components/tools/research-workflow/check-parts";
import {
  DocumentActions,
  DocumentPreview,
} from "@/components/tools/research-workflow/document-parts";
import {
  type FieldSpec,
  SpecFields,
} from "@/components/tools/research-workflow/form-parts";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { isoDateToLocal } from "@/lib/tool-kit/research-workflow/dates";
import {
  buildDebriefing,
  checkDebriefing,
  type DebriefInput,
  debriefProse,
  EMPTY_DEBRIEF_INPUT,
  type WithdrawalMode,
} from "@/lib/tool-kit/research-workflow/debriefing";
import {
  docToHtml,
  docToText,
} from "@/lib/tool-kit/research-workflow/document";

/** English strings. A translated content file overrides these through `ui`. */
export const DEBRIEFING_FORM_GENERATOR_STRINGS = {
  studyPanel: "The study and who to contact",
  purposePanel: "What the study was really about",
  deceptionPanel: "Deception",
  dataPanel: "Data, support and further reading",
  titleLabel: "Study title",
  titlePlaceholder: "e.g. Memory for Faces Under Time Pressure",
  researcherLabel: "Researcher's name",
  researcherPlaceholder: "e.g. Priya Shah",
  institutionLabel: "Institution",
  institutionPlaceholder: "e.g. Northfield College",
  contactLabel: "Researcher's email or phone",
  contactPlaceholder: "e.g. p.shah@northfield.example.edu",
  irbLabel: "IRB or research ethics office",
  irbPlaceholder: "e.g. the Northfield College Research Ethics Board",
  irbContactLabel: "IRB email or phone",
  irbContactPlaceholder: "e.g. ethics@northfield.example.edu",
  purposeLabel: "The study's true purpose",
  purposePlaceholder:
    "e.g. This study tested whether time pressure changes how accurately people recognise faces they have seen before.",
  hypothesesLabel: "What you expected to find — one per line (optional)",
  hypothesesPlaceholder:
    "e.g. People with less time will recognise fewer faces.",
  conditionsLabel: "Groups or conditions (optional)",
  conditionsPlaceholder:
    "e.g. You were assigned by chance to a timed or an untimed group.",
  deceptionCheck: "The study used deception or held back its real purpose",
  coverStoryLabel: "What participants were told at the start",
  coverStoryPlaceholder:
    "e.g. The study was described as research on first impressions of personality.",
  withheldLabel: "What was held back or not accurate",
  withheldPlaceholder:
    "e.g. The real aim was to test memory: the second set of photos included faces you had already seen.",
  justificationLabel: "Why deception was necessary",
  justificationPlaceholder:
    "e.g. If you had known your memory was being tested, you might have tried to memorise the faces, which would have changed the results.",
  withdrawalLabel: "How people can withdraw their data",
  withdrawContactOption: "By contacting the researcher",
  withdrawHereOption: "By choosing on this page",
  withdrawNoneOption: "Not possible — responses are anonymous",
  deadlineLabel: "Withdrawal deadline (optional)",
  resultsDateLabel: "Results summary available after (optional)",
  confidentialityLabel: "Confidentiality reminder (optional)",
  confidentialityPlaceholder:
    "e.g. Your responses are stored under a participant number, not your name.",
  noDiscussionCheck:
    "Ask participants not to discuss the study with others who may take part",
  supportLabel: "Support services — one per line",
  supportPlaceholder:
    "e.g. Northfield College Counseling Center — 555-0100\n988 Suicide & Crisis Lifeline — call or text 988 (U.S.)",
  readingLabel: "Further reading — one reference per line (optional)",
  readingPlaceholder:
    "e.g. Schacter, D. L. (2001). The seven sins of memory: How the mind forgets and remembers. Houghton Mifflin.",
  irbReminder:
    "A draft for your IRB or ethics committee to review. If your institution has a debriefing template, paste these sections into it. Debrief promptly — at the end of each session, and no later than the end of data collection.",
  anonymousDeceptionWarning:
    "APA Standard 8.07(c) asks that people who were deceived be allowed to withdraw their data. If responses are anonymous, offer the choice on this page instead, before the data are submitted — choose “By choosing on this page”.",
  checklistHeading: "Debriefing checklist",
  missingCount:
    "one:{count} item still needs your words.|other:{count} items still need your words.",
  allCovered:
    "Everything APA Standards 8.07(c) and 8.08 ask of a debriefing is covered.",
  statusCovered: "Covered",
  statusMissing: "Needs your words",
  statusStandard: "Standard wording",
  statusNa: "Not applicable",
  elements: {
    purpose: "True purpose of the study — APA 8.08(a)",
    deception: "Deception explained, with the reason — 8.07(c)",
    withdrawal: "A way to withdraw data — 8.07(c)",
    results: "How to get the results — 8.08(a)",
    support: "Support for anyone distressed — 8.08(c)",
    contacts: "Contacts for questions and concerns",
  },
  plainHeading: "Plain-language check",
  longCount:
    "one:{count} sentence runs past 25 words:|other:{count} sentences run past 25 words:",
  longItem: "one:“{start} …” — {count} word|other:“{start} …” — {count} words",
  jargonLead: "Plainer words for participants:",
  jargonItem: "{term} → {plain}",
  plainClear: "No long sentences or flagged jargon in what you have written.",
  jargon: [
    "confederate = a member of the research team posing as a participant",
    "manipulation = the change we made between groups",
    "independent variable = what we changed",
    "dependent variable = what we measured",
    "hypothesis = what we expected to find",
    "hypotheses = what we expected to find",
    "condition = group",
    "conditions = groups",
    "counterbalanced = shown in different orders",
    "cover story = the explanation you were given",
    "randomly assigned = assigned by chance",
    "randomized = assigned by chance",
    "statistically significant = unlikely to be due to chance alone",
    "stimuli = the pictures, words or sounds you saw or heard",
    "paradigm = task",
    "utilize = use",
    "prior to = before",
  ],
  previewHeading: "Your debriefing statement",
  previewLabel: "Debriefing statement preview",
  copyText: "Copy as text",
  copyFormatted: "Copy for Word or Google Docs",
  copied: "Copied!",
  downloadText: "Download .txt",
  docTitle: "Debriefing Statement",
  metaStudy: "Study title: {value}",
  metaResearcher: "Researcher: {value}",
  metaInstitution: "Institution: {value}",
  thanksHeading: "Thank you",
  thanksPlain:
    "Thank you for taking part in this study. This page explains what the study was about and what happens next.",
  thanksDeception:
    "Thank you for taking part in this study. This page explains what the study was really about — including something we could not tell you at the start — and what happens next.",
  purposeHeading: "What was the study about?",
  hypothesesLead: "We expected that:",
  deceptionHeading: "What we could not tell you at the start",
  coverLead: "What you were told at the start:",
  withheldLead: "What was actually happening:",
  whyLead: "Why we could not tell you then:",
  apologySentence:
    "We are sorry for not telling you everything at the start. If you feel uncomfortable about it, please tell us — and you can still choose to withdraw your data.",
  dataHeading: "What happens to your data",
  withdrawContact:
    "If you would like your data withdrawn from the study, contact {researcher} at {contact}. Your data will be deleted, and there is no penalty for withdrawing.",
  withdrawContactBy:
    "If you would like your data withdrawn from the study, contact {researcher} at {contact} by {date}. Your data will be deleted, and there is no penalty for withdrawing.",
  withdrawHere:
    "Now that you know the full purpose of the study, you can choose below whether we may use your data. If you choose to withdraw, your data will be deleted, and there is no penalty.",
  withdrawNone:
    "Your responses were anonymous, so they cannot be linked to you or withdrawn after you submitted them.",
  discussionHeading: "Please keep the details to yourself",
  discussionSentence:
    "Other people may still take part in this study. Please do not discuss its purpose or procedures with anyone who might take part — knowing in advance could change how they respond.",
  supportHeading: "If you feel upset",
  supportLead:
    "If taking part has left you feeling uncomfortable or distressed, please contact {researcher} at {contact}, or one of these services:",
  readingHeading: "Want to know more?",
  readingLead: "If you would like to read more about this topic:",
  resultsSentence:
    "If you would like a summary of the results, contact {researcher} at {contact}.",
  resultsSentenceAfter:
    "If you would like a summary of the results, contact {researcher} at {contact}. A summary will be available after {date}.",
  contactHeading: "Questions or concerns",
  contactStudy:
    "If you have questions about this study, contact {researcher} at {contact}.",
  contactRights:
    "If you have concerns about your rights as a participant, or about how the study was run, contact {irb} at {irbContact}.",
  choiceHeading: "Your choice about your data",
  choiceLead: "Please tick one box.",
  choiceKeep: "☐  I agree that my data may be used in this study.",
  choiceWithdraw: "☐  I want my data withdrawn and deleted.",
  missingTitle: "[study title]",
  missingResearcher: "[researcher's name]",
  missingContact: "[email or phone]",
  missingIrb: "[IRB or ethics office]",
  missingIrbContact: "[IRB email or phone]",
  missingPurpose: "[Explain the real purpose of the study in plain words.]",
  missingWithheld: "[Explain what was held back or was not true.]",
  missingJustification:
    "[Explain why the study could not be done without deception.]",
  missingSupport:
    "[List at least one support service, such as your institution's counseling center.]",
} as const;

type Strings = typeof DEBRIEFING_FORM_GENERATOR_STRINGS;
type Spec = FieldSpec<DebriefInput, Strings>;

const STUDY_FIELDS: Spec[] = [
  ["title", "titleLabel", "titlePlaceholder", false],
  ["researcher", "researcherLabel", "researcherPlaceholder", false],
  ["institution", "institutionLabel", "institutionPlaceholder", false],
  ["contact", "contactLabel", "contactPlaceholder", false],
  ["irbName", "irbLabel", "irbPlaceholder", false],
  ["irbContact", "irbContactLabel", "irbContactPlaceholder", false],
];

const PURPOSE_FIELDS: Spec[] = [
  ["purpose", "purposeLabel", "purposePlaceholder", true],
  ["hypotheses", "hypothesesLabel", "hypothesesPlaceholder", true],
  ["conditions", "conditionsLabel", "conditionsPlaceholder", true],
];

const DECEPTION_FIELDS: Spec[] = [
  ["coverStory", "coverStoryLabel", "coverStoryPlaceholder", true],
  ["withheld", "withheldLabel", "withheldPlaceholder", true],
  ["justification", "justificationLabel", "justificationPlaceholder", true],
];

const DATA_FIELDS: Spec[] = [
  [
    "confidentiality",
    "confidentialityLabel",
    "confidentialityPlaceholder",
    true,
  ],
  ["support", "supportLabel", "supportPlaceholder", true],
  ["reading", "readingLabel", "readingPlaceholder", true],
];

export default function DebriefingFormGeneratorWidget() {
  const strings = useToolUi(DEBRIEFING_FORM_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<DebriefInput>(EMPTY_DEBRIEF_INPUT);
  const [deadline, setDeadline] = useState("");
  const [resultsDate, setResultsDate] = useState("");

  const set = <K extends keyof DebriefInput>(key: K, value: DebriefInput[K]) =>
    setInput((current) => ({ ...current, [key]: value }));

  const resolved = useMemo<DebriefInput>(
    () => ({
      ...input,
      deadline: isoDateToLocal(deadline, locale),
      resultsDate: isoDateToLocal(resultsDate, locale),
    }),
    [input, deadline, resultsDate, locale]
  );
  const doc = useMemo(
    () => buildDebriefing(resolved, strings),
    [resolved, strings]
  );
  const checks = useMemo(() => checkDebriefing(resolved), [resolved]);
  const missing = checks.filter((check) => check.status === "missing").length;
  const text = useMemo(() => docToText(doc), [doc]);
  const html = useMemo(() => docToHtml(doc), [doc]);
  const blocked = input.deception && input.withdrawal === "none";
  const fields = { input, onChange: set, strings, prefix: "dbf" } as const;

  return (
    <div className="space-y-6">
      <Notice>{strings.irbReminder}</Notice>
      <Panel title={strings.studyPanel}>
        <div className="grid gap-4 sm:grid-cols-2">
          <SpecFields specs={STUDY_FIELDS} {...fields} />
        </div>
      </Panel>
      <Panel title={strings.purposePanel}>
        <SpecFields specs={PURPOSE_FIELDS} {...fields} />
      </Panel>
      <Panel title={strings.deceptionPanel}>
        <Checkbox
          checked={input.deception}
          label={strings.deceptionCheck}
          onChange={(value) => set("deception", value)}
        />
        {input.deception ? (
          <SpecFields specs={DECEPTION_FIELDS} {...fields} />
        ) : null}
      </Panel>
      <Panel title={strings.dataPanel}>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField<WithdrawalMode>
            id="dbf-withdrawal"
            label={strings.withdrawalLabel}
            onChange={(value) => set("withdrawal", value)}
            options={[
              { value: "contact", label: strings.withdrawContactOption },
              { value: "here", label: strings.withdrawHereOption },
              { value: "none", label: strings.withdrawNoneOption },
            ]}
            value={input.withdrawal}
          />
          {input.withdrawal === "contact" ? (
            <TextField
              id="dbf-deadline"
              label={strings.deadlineLabel}
              onChange={setDeadline}
              type="date"
              value={deadline}
            />
          ) : null}
          <TextField
            id="dbf-results-date"
            label={strings.resultsDateLabel}
            onChange={setResultsDate}
            type="date"
            value={resultsDate}
          />
        </div>
        {blocked ? (
          <p className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-foreground text-sm">
            {strings.anonymousDeceptionWarning}
          </p>
        ) : null}
        <SpecFields specs={DATA_FIELDS} {...fields} />
        <Checkbox
          checked={input.noDiscussion}
          label={strings.noDiscussionCheck}
          onChange={(value) => set("noDiscussion", value)}
        />
      </Panel>

      <section className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.checklistHeading}</h2>
        <p aria-live="polite" className="text-foreground text-sm">
          {missing > 0
            ? tPlural(strings.missingCount, locale, missing, {
                count: fmt(missing),
              })
            : strings.allCovered}
        </p>
        <StatusList
          items={checks.map((check) => ({
            id: check.element,
            label: strings.elements[check.element],
            status: check.status,
          }))}
          labels={{
            covered: strings.statusCovered,
            missing: strings.statusMissing,
            standard: strings.statusStandard,
            na: strings.statusNa,
          }}
        />
      </section>

      <PlainLanguagePanel
        extraWarning={null}
        heading={strings.plainHeading}
        jargon={strings.jargon}
        labels={strings}
        locale={locale}
        text={debriefProse(input)}
      />

      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.previewHeading}</h2>
        <DocumentActions
          filename="debriefing-statement"
          html={html}
          labels={strings}
          text={text}
        />
        <DocumentPreview doc={doc} label={strings.previewLabel} />
      </section>
    </div>
  );
}
